import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { fieldControlStatus, useFieldStatus } from './field'

/**
 * Textarea — multi-line text control, from the Figma DS2026 "Text area" spec
 * (First Bite Design System, node 7148:6098). Shares the Input look (white
 * surface, gray-300 border, 8px radius, status border + 4px focus ring) with a
 * taller default (`min-h-20` ≈ 80px) and vertical resize.
 *
 *   status  default | error | success
 *   size    sm | md                       (64px/12px text · 80px/14px text)
 *
 * Inside a `<Field>` the status is inherited from context; pass `status` here to
 * override it. Pair with `<FieldLabel>` / `<FieldHint>` for labelled layouts.
 */
export const textareaVariants = cva(
  cn(
    'w-full resize-y rounded-lg border bg-fbui-base-white',
    'text-fbui-gray-800 tracking-[-0.2px] placeholder:text-fbui-gray-400',
    'outline-none transition focus:ring-4',
    'disabled:cursor-not-allowed disabled:resize-none disabled:bg-fbui-gray-50 disabled:text-fbui-text-disabled disabled:border-fbui-gray-200'
  ),
  {
    variants: {
      status: fieldControlStatus,
      size: {
        sm: 'min-h-16 px-3 py-1.5 text-xs',
        md: 'min-h-20 px-3.5 py-2.5 text-sm',
      },
    },
    defaultVariants: { status: 'default', size: 'md' },
  }
)

export interface TextareaProps
  extends
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    Omit<VariantProps<typeof textareaVariants>, 'status'> {
  /** Border + ring color. Inherited from a parent `<Field>` when omitted. */
  status?: VariantProps<typeof textareaVariants>['status']
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, status, size, rows = 3, ...props }, ref) => {
    const resolved = useFieldStatus(status ?? undefined)
    return (
      <textarea
        ref={ref}
        rows={rows}
        aria-invalid={resolved === 'error' || undefined}
        className={cn(textareaVariants({ status: resolved, size, className }))}
        {...props}
      />
    )
  }
)
Textarea.displayName = 'Textarea'
